import type { EditorState } from '@codemirror/state';
import type { EditorView, ViewUpdate } from '@codemirror/view';
import {
  contentHash,
  DEFAULT_FORMAT,
  EMPTY_HASH,
  kindFromName,
  nextUntitledName,
  renamedFile,
  saveStatus,
  textHash,
  type CloudLink,
  type DocKind,
  type DocMeta,
} from '../core/documents';
import { adoptedFields, isCloudOnly, isUnprotected, linkFromRemote, needsPush, type CloudMeta } from '../core/cloud';
import { createSyncEngine, type SyncDoc, type SyncEngine, type SyncHost, type SyncState } from '../cloud/engine';
import { offCloud, type Cloud } from '../cloud';
import type { RemoteDoc } from '../cloud/api';
import { defaultRect, MAX_WINDOWS, normalizeRect, placeNew, zoneRect, type Rect, type Size, type Zone } from '../core/geometry';
import { arrange, layoutRect, matchSize, readingOrder, type Arrangement, type WindowLayout } from '../core/layouts';
import {
  emptySession,
  focusedWindow,
  neighbourId,
  sessionReducer,
  windowOf,
  type SessionAction,
  type SessionState,
  type WindowState,
} from '../core/session';
import { createStore, type Store } from '../core/store';
import { createDocState, onEditorUpdate, reconfigureKind, setEditorPrefs } from '../editor/setup';
import { formatState, NO_FORMAT, sameFormat } from '../editor/markdownCommands';
import { encodeText } from '../platform/codec';
import { FileConflictError, FilePermissionError, type FileAdapter, type FileRef, type OpenResult } from '../platform/files';
import { clearLocalKeys, savePrefs, type EditorPrefs } from '../platform/prefs';
import type { LegacySession, SessionStorage, StoredDocument, StoredSession } from '../platform/storage';
import { isReduced } from '../motion/motion';
import { ask, askWithOption, notify, patchUi, ui } from './ui';

/**
 * The application controller: the one place where document state, editor
 * states, windows, session persistence and file capabilities meet. UI
 * components call these operations; they never touch storage or files
 * directly.
 *
 * Each window has its own EditorView. A window with no documents still has a
 * live editor showing an empty "scratch" state: the first keystroke — or the
 * first IME composition — lands in a real editor and becomes a document
 * without the view ever being swapped, so no input is lost.
 */

const PERSIST_DELAY = 400;
const canRecede = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;

export interface Controller {
  session: ReturnType<typeof createStore<SessionState>>;
  /** Saving writes back to linked files (otherwise it produces downloads). */
  writesInPlace: boolean;
  init(): Promise<void>;
  attachView(windowId: string, view: EditorView | null): void;
  /** The editor of a window (default: the focused one). */
  view(windowId?: string): EditorView | null;
  newDocument(kind: DocKind, text?: string, windowId?: string): string;
  openFiles(windowId?: string): Promise<void>;
  openDropped(data: DataTransfer, target?: { windowId: string } | { point: { x: number; y: number } }): Promise<void>;
  activate(id: string, options?: { focus?: boolean }): void;
  cycle(direction: 1 | -1): void;
  close(id?: string | null): Promise<boolean>;
  closeOthers(id: string): Promise<void>;
  save(id?: string | null): Promise<boolean>;
  saveAs(id?: string | null): Promise<boolean>;
  rename(id: string, base: string): boolean;
  setKind(id: string, kind: DocKind): void;
  reorder(id: string, index: number): void;
  moveToNewWindow(id: string): string | null;
  /** Move a document into another window (on a phone: another workspace). */
  moveToWindow(id: string, windowId: string): boolean;
  /** Name a window's workspace; an empty name clears it. Device-local; seeds the cloud workspace's name. */
  renameWorkspace(windowId: string, name: string): void;
  text(id: string): string;
  focusEditor(windowId?: string): void;
  newWindow(options?: { rect?: Rect; quiet?: boolean }): string | null;
  closeWindow(id: string): Promise<boolean>;
  focusWindow(id: string): void;
  placeWindow(id: string, rect: Rect, zone: Zone | null, restore?: Rect | null): void;
  toggleMaximize(id: string): void;
  tidyWindows(): void;
  /** Put one window into a layout (a half, a quarter, a third, centred…). Applying it again restores. */
  layoutWindow(id: string, layout: WindowLayout): void;
  /** Arrange every window on the canvas. */
  arrangeWindows(mode: Arrangement): void;
  /** Give every other window the focused window's size. */
  matchWindowSizes(id: string): void;
  /** Return to the layout before the last arrangement. */
  undoLayout(): boolean;
  canUndoLayout(): boolean;
  /** A window was moved or resized by hand: automatic arrangement steps aside. */
  windowPlacedByHand(): void;
  /**
   * Development tool: erase MyTXT's own browser data (open documents and drafts,
   * windows, session; optionally preferences). Never touches files on disk.
   */
  resetLocalState(options: { prefs: boolean }): Promise<void>;
  /** Re-fit every window after the canvas changed size. */
  fitToCanvas(): void;
  canvas(): Size;
  setPrefs(patch: Partial<EditorPrefs>): void;
  hasUnprotectedWork(): boolean;
  flush(): Promise<void>;
  /** Everything about the cloud: account, synchronisation, library. */
  cloud: CloudControls;
}

/** A document kept on this device although its tab is closed, because the cloud does not hold its latest words yet. */
export interface UnsyncedDoc {
  id: string;
  name: string;
}

export interface MoveReport {
  id: string;
  name: string;
  status: 'verified' | 'failed' | 'skipped';
  detail: string;
}

export interface CloudControls {
  /** A backend is configured in this build. */
  readonly configured: boolean;
  readonly problem: string | null;
  readonly auth: Cloud['auth'];
  /** The synchronisation engine's state; null without a signed-in account. */
  readonly sync: Store<SyncState | null>;
  /** Closed documents still waiting to reach the cloud. */
  readonly unsynced: Store<UnsyncedDoc[]>;
  /** Push what is pending and look for changes made elsewhere. */
  syncNow(): Promise<void>;
  /** Open (or bring forward) a cloud document. */
  open(id: string): Promise<boolean>;
  /** Move documents from this device into the cloud library, verifying each by reading it back. Nothing is removed locally. */
  moveToCloud(ids: string[]): Promise<MoveReport[]>;
  /** Delete a document from the cloud library on every device (after confirming). */
  deleteFromCloud(id: string): Promise<boolean>;
  /** Sign out, keeping unsynced words safe on this device. False if the person cancelled. */
  signOut(): Promise<boolean>;
}

export interface ControllerDeps {
  storage: SessionStorage;
  files: FileAdapter;
  newId?: () => string;
  /** The size of the canvas windows live on. */
  canvas?: () => Size;
  /** Accounts and cloud documents. Without it MyTXT is a local editor. */
  cloud?: Cloud;
}

/** A version-4 UUID: cloud ids must be UUIDs, and `randomUUID` exists only in secure contexts. */
function randomId(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const browserCanvas = (): Size => ({ w: globalThis.innerWidth || 1280, h: globalThis.innerHeight || 800 });

export function createController(deps: ControllerDeps): Controller {
  const { storage, files } = deps;
  const newId = deps.newId ?? randomId;
  const canvas = deps.canvas ?? browserCanvas;

  const session = createStore<SessionState>(emptySession(defaultRect(canvas())));
  const states = new Map<string, EditorState>();
  const refs = new Map<string, FileRef>();
  const scrollTops = new Map<string, number>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Documents whose latest text has not been handed to storage yet. */
  const pendingText = new Set<string>();
  const inflight = new Set<Promise<unknown>>();
  const views = new Map<string, EditorView>();
  const windowOfView = new Map<EditorView, string>();
  /** What each window's view currently shows: a document id, or null for the scratch state. */
  const shown = new Map<string, string | null>();
  /** Windows whose editor should take focus as soon as it exists. */
  const focusOnAttach = new Set<string>();
  let lastFocused: string | null = null;
  let sessionWriteQueued = false;
  let storageFailed = false;
  /** Set while local state is being erased: nothing may be written back. */
  let resetting = false;
  /** Window geometry before the last arrangement, to undo it. */
  let layoutHistory: Array<Pick<WindowState, 'id' | 'rect' | 'zone' | 'restore'>> | null = null;

  // Cloud state. Local persistence, cloud synchronisation and files are three separate things:
  // the first is everything above, the second lives in `engine`, the third in `files`.
  const cloud = deps.cloud ?? offCloud();
  /** Closed cloud documents whose latest words the cloud does not hold yet. They keep syncing; closing a tab never loses them. */
  const detached = new Map<string, { meta: CloudMeta; text: string }>();
  const unsyncedStore = createStore<UnsyncedDoc[]>([]);
  const syncStore = createStore<SyncState | null>(null);
  let engine: SyncEngine | null = null;
  let account: string | null = null;
  /** True while the engine replaces a document's text: that is the cloud's edit, not the person's. */
  let adopting = false;
  let lastResume = 0;

  const dispatch = (action: SessionAction) => session.set((s) => sessionReducer(s, action));
  const meta = (id: string | null | undefined) => {
    const s = session.get();
    const docId = id ?? focusedWindow(s)?.activeId;
    return s.docs.find((d) => d.id === docId) ?? null;
  };
  const win = (id: string) => session.get().windows.find((w) => w.id === id) ?? null;
  const stateOf = (id: string) => states.get(id)!;
  const text = (id: string) => states.get(id)?.doc.toString() ?? '';

  // --- persistence ---------------------------------------------------------

  function track<T>(promise: Promise<T>): Promise<T> {
    const tracked = promise.catch((error) => {
      if (!storageFailed) {
        storageFailed = true;
        notify('Drafts can’t be kept in this browser right now. Save to a file to keep your work.', 'error');
      }
      console.error(error);
    }) as Promise<T>;
    inflight.add(tracked);
    tracked.finally(() => inflight.delete(tracked));
    return tracked;
  }

  function recordFor(id: string): StoredDocument | null {
    const m = meta(id);
    const state = states.get(id);
    if (!m || !state) return null;
    const { anchor, head } = state.selection.main;
    return { meta: m, text: state.doc.toString(), selection: { anchor, head }, ref: refs.get(id) };
  }

  function writeDocument(id: string) {
    clearTimeout(timers.get(id));
    timers.delete(id);
    if (resetting) return;
    reconcileDirty(id);
    reconcileCloud(id);
    // Saves, renames and new content all pass through here: tell the engine what the cloud is missing.
    if (meta(id)?.unsynced) engine?.touch(id);
    const record = recordFor(id);
    pendingText.delete(id);
    if (record) track(storage.putDocument(record));
  }

  function schedulePersist(id: string) {
    clearTimeout(timers.get(id));
    timers.set(id, setTimeout(() => writeDocument(id), PERSIST_DELAY));
  }

  function persistSession() {
    if (sessionWriteQueued || resetting) return;
    sessionWriteQueued = true;
    queueMicrotask(() => {
      sessionWriteQueued = false;
      if (resetting) return;
      const { windows, focusedId } = session.get();
      const record: StoredSession = {
        version: 2,
        windows: windows.map(({ id, docIds, activeId, rect, zone, restore, z, workspaceId, workspaceAccount, name }) => ({ id, docIds, activeId, rect, zone, restore, z, workspaceId, workspaceAccount, name })),
        focusedId,
        canvas: canvas(),
      };
      track(storage.putSession(record));
    });
  }

  /** Exact dirty state: equal to the last file content again (e.g. after undo) means clean. */
  function reconcileDirty(id: string) {
    const m = meta(id);
    if (!m) return;
    const dirty = textHash(text(id)) !== m.savedHash;
    if (dirty !== m.dirty) dispatch({ type: 'patch', id, patch: { dirty } });
  }

  async function flush() {
    for (const id of [...timers.keys()]) writeDocument(id);
    // Best effort: the page may be going away. Local persistence above is what guarantees the words.
    void engine?.pushPending();
    await Promise.all([...inflight]);
  }

  // --- editors ---------------------------------------------------------------

  /** Show a document — or, for `null`, a fresh scratch state — in a window's view. */
  function show(windowId: string, id: string | null) {
    const view = views.get(windowId);
    if (!view) return;
    const previous = shown.get(windowId);
    if (previous && states.has(previous)) scrollTops.set(previous, view.scrollDOM.scrollTop);
    shown.set(windowId, id);
    if (!id) {
      view.setState(createDocState('', 'md'));
    } else {
      view.setState(stateOf(id));
      const top = scrollTops.get(id) ?? 0;
      view.scrollDOM.scrollTop = top;
      if (!scrollTops.has(id)) view.dispatch({ effects: [], scrollIntoView: true });
    }
    if (windowId === session.get().focusedId) publishFocused();
  }

  function publishFormat(state: EditorState, kind: DocKind) {
    const next = kind === 'md' ? formatState(state) : NO_FORMAT;
    if (!sameFormat(next, ui.get().format)) patchUi({ format: next });
  }

  /** Format and selection state always describe the focused window. */
  function publishFocused() {
    const view = views.get(session.get().focusedId);
    if (!view) return;
    const docId = shown.get(session.get().focusedId);
    publishFormat(view.state, docId ? (meta(docId)?.kind ?? 'txt') : 'md');
    patchUi({ selectionEmpty: view.state.selection.main.empty, selectionTick: ui.get().selectionTick + 1, linkEditor: null });
  }

  /** The first input in an empty window becomes a Markdown document — in place, mid-keystroke. */
  function materialize(windowId: string, state: EditorState) {
    const id = newId();
    states.set(id, state);
    shown.set(windowId, id);
    const name = nextUntitledName(session.get().docs.map((d) => d.name), 'md');
    dispatch({ type: 'add', windowId, doc: { id, name, kind: 'md', origin: 'new', dirty: true, savedHash: EMPTY_HASH, lastSave: 'none', format: DEFAULT_FORMAT, ...cloudFieldsForNew(windowId, 'md') } });
    patchUi({ intro: 'none' });
    return id;
  }

  onEditorUpdate((u: ViewUpdate) => {
    const windowId = windowOfView.get(u.view);
    if (!windowId) return;
    let id = shown.get(windowId) ?? null;
    if (!id) {
      if (!u.docChanged) {
        if (u.selectionSet && windowId === session.get().focusedId) publishFocused();
        return;
      }
      id = materialize(windowId, u.state);
    }
    states.set(id, u.state);
    const m = meta(id);
    if (u.docChanged) {
      if (m && !m.dirty) dispatch({ type: 'patch', id, patch: { dirty: true } });
      if (m?.cloud && !adopting) {
        if (!m.unsynced) dispatch({ type: 'patch', id, patch: { unsynced: true } });
        engine?.touch(id);
      }
      pendingText.add(id);
      schedulePersist(id);
      // Chrome recedes while typing — only where a hovering pointer can call it back.
      if (canRecede && u.transactions.some((tr) => tr.isUserEvent('input.type') || tr.isUserEvent('delete'))) patchUi({ typing: true });
    } else if (u.selectionSet) {
      schedulePersist(id);
    }
    if ((u.docChanged || u.selectionSet) && windowId === session.get().focusedId) {
      publishFormat(u.state, m?.kind ?? 'txt');
      patchUi({ selectionEmpty: u.state.selection.main.empty, selectionTick: ui.get().selectionTick + 1 });
    }
    // Focusing an editor focuses its window.
    if (u.focusChanged && u.view.hasFocus && windowId !== session.get().focusedId) focusWindow(windowId);
  });

  // Keep every view showing its window's active document, however that changed.
  session.subscribe(() => {
    const s = session.get();
    for (const w of s.windows) {
      if (views.has(w.id) && w.activeId !== (shown.get(w.id) ?? null)) show(w.id, w.activeId);
    }
    if (s.focusedId !== lastFocused) {
      lastFocused = s.focusedId;
      publishFocused();
    }
    persistSession();
    const m = meta(null);
    if (typeof document !== 'undefined') document.title = m ? `${isUnprotected(m) ? '• ' : ''}${m.name} — MyTXT` : 'MyTXT';
  });

  function focusEditor(windowId?: string) {
    // Look the view up when the frame comes: a window that just mounted may have replaced it since.
    requestAnimationFrame(() => views.get(windowId ?? session.get().focusedId)?.focus());
  }

  // --- documents -----------------------------------------------------------

  function addDocument(doc: DocMeta, content: string, options: { selection?: { anchor: number; head: number }; ref?: FileRef; focus?: boolean; windowId?: string; activate?: boolean } = {}) {
    states.set(doc.id, createDocState(content, doc.kind, options.selection));
    if (options.ref !== undefined) refs.set(doc.id, options.ref);
    dispatch({ type: 'add', doc, windowId: options.windowId, activate: options.activate });
    writeDocument(doc.id);
    patchUi({ intro: 'none' });
    if (options.focus !== false) focusEditor(options.windowId);
  }

  function newDocument(kind: DocKind, content = '', windowId?: string) {
    const id = newId();
    const name = nextUntitledName(session.get().docs.map((d) => d.name), kind);
    const target = windowId ?? session.get().focusedId;
    const doc: DocMeta = { id, name, kind, origin: 'new', dirty: content !== '', savedHash: EMPTY_HASH, lastSave: 'none', format: DEFAULT_FORMAT, ...cloudFieldsForNew(target, kind) };
    if (windowId) dispatch({ type: 'focusWindow', id: windowId });
    addDocument(doc, content, { ...(content ? { selection: { anchor: content.length, head: content.length } } : {}), windowId: target });
    return id;
  }

  /** A fresh, untouched "Untitled" is replaced by a file opened on top of it. */
  function blankActive(windowId: string): DocMeta | null {
    const m = meta(win(windowId)?.activeId);
    return m && saveStatus(m) === 'blank' && text(m.id) === '' ? m : null;
  }

  async function receive(result: OpenResult, windowId: string) {
    const placeholder = blankActive(windowId);
    let opened = 0;
    let lastId: string | null = null;
    for (const file of result.files) {
      // Don't open the same file twice; bring the existing tab (and its window) forward.
      let existing: DocMeta | undefined;
      for (const d of session.get().docs) {
        const ref = refs.get(d.id);
        if (file.ref !== undefined && ref !== undefined && (await files.sameFile(ref, file.ref))) existing = d;
        else if (file.ref === undefined && d.origin === 'import' && d.name === file.name && !d.dirty && text(d.id) === file.decoded.text) existing = d;
        if (existing) break;
      }
      if (existing) {
        lastId = existing.id;
        continue;
      }
      const id = newId();
      addDocument(
        {
          id,
          name: file.name,
          kind: kindFromName(file.name),
          origin: file.ref !== undefined ? 'file' : 'import',
          dirty: false,
          savedHash: textHash(file.decoded.text),
          lastSave: 'none',
          format: file.decoded.format,
          fileModified: file.lastModified,
        },
        file.decoded.text,
        { ref: file.ref, focus: false, selection: { anchor: 0, head: 0 }, windowId },
      );
      if (file.decoded.legacyEncoding) notify(`Opened “${file.name}” as ${file.decoded.legacyEncoding}. Saving will write UTF-8.`);
      opened++;
      lastId = id;
    }
    if (lastId) dispatch({ type: 'activate', id: lastId });
    if (opened && placeholder && placeholder.id !== lastId) await discard(placeholder.id);
    if (result.rejected.length) {
      const names = result.rejected.map((n) => `“${n}”`).join(', ');
      notify(`${names} ${result.rejected.length === 1 ? 'isn’t' : 'aren’t'} plain text and can’t be opened.`, 'error');
    }
    if (lastId) focusEditor();
    return opened + (lastId ? 1 : 0);
  }

  async function openFiles(windowId?: string) {
    try {
      await receive(await files.pickFiles(), windowId ?? session.get().focusedId);
    } catch (error) {
      console.error(error);
      notify('That file couldn’t be opened.', 'error');
    }
  }

  async function openDropped(data: DataTransfer, target?: { windowId: string } | { point: { x: number; y: number } }) {
    try {
      // Read synchronously-sensitive drop data before anything else happens.
      const result = files.filesFromDrop(data);
      let windowId = target && 'windowId' in target ? target.windowId : session.get().focusedId;
      if (target && 'point' in target) {
        // Files dropped on the open canvas get a window of their own, right there.
        const size = focusedWindow(session.get())?.rect ?? defaultRect(canvas());
        const rect = normalizeRect({ x: target.point.x - size.w / 2, y: target.point.y - 24, w: size.w, h: size.h }, null, canvas());
        windowId = newWindow({ rect, quiet: true }) ?? windowId;
      }
      dispatch({ type: 'focusWindow', id: windowId });
      await receive(await result, windowId);
    } catch (error) {
      console.error(error);
      notify('That file couldn’t be opened.', 'error');
    }
  }

  function activate(id: string, options: { focus?: boolean } = {}) {
    dispatch({ type: 'activate', id });
    if (options.focus !== false) focusEditor();
  }

  function cycle(direction: 1 | -1) {
    const id = neighbourId(session.get(), direction);
    if (id) activate(id);
  }

  function forget(id: string) {
    clearTimeout(timers.get(id));
    timers.delete(id);
    pendingText.delete(id);
    states.delete(id);
    refs.delete(id);
    scrollTops.delete(id);
  }

  async function discard(id: string) {
    const m = meta(id);
    // A cloud document whose latest words the cloud lacks is not discarded: it moves to the unsynced set.
    const keep = m ? retire(id, m) : null;
    // The session subscription swaps the neighbouring tab (or the scratch state) into the view.
    dispatch({ type: 'close', id });
    forget(id);
    if (keep) {
      await track(storage.putDocument(keep));
      engine?.touch(id);
    } else {
      await track(storage.deleteDocument(id));
    }
  }

  /** Ask about unsaved changes. Resolves true when the document may go. */
  async function confirmLeave(id: string): Promise<boolean> {
    reconcileDirty(id);
    const current = meta(id);
    if (!current?.dirty) return true;
    const neverSaved = saveStatus(current) === 'draft';
    const choice = await ask({
      title: `Save changes to “${current.name}”?`,
      message: neverSaved ? 'This document has never been saved to a file. Closing it discards its text.' : 'Your changes since the last save will be lost.',
      actions: [
        { id: 'discard', label: 'Don’t Save', tone: 'danger' },
        { id: 'cancel', label: 'Cancel' },
        { id: 'save', label: files.writesInPlace || current.origin !== 'file' ? 'Save…' : 'Save', tone: 'primary' },
      ],
      cancelId: 'cancel',
    });
    if (choice === 'cancel') return false;
    if (choice === 'save') return save(current.id);
    return true;
  }

  async function close(id?: string | null) {
    const m = meta(id);
    if (!m) return false;
    const owner = windowOf(session.get(), m.id);
    if (owner && owner.id !== session.get().focusedId) dispatch({ type: 'focusWindow', id: owner.id });
    // A cloud-only document is protected by the cloud, or kept on this device until it is: closing asks nothing.
    if (!isCloudOnly(m) && (meta(m.id)?.dirty || textHash(text(m.id)) !== m.savedHash)) {
      if (owner?.activeId !== m.id) dispatch({ type: 'activate', id: m.id });
      if (!(await confirmLeave(m.id))) {
        focusEditor();
        return false;
      }
    }
    const wasActive = owner?.activeId === m.id;
    await discard(m.id);
    if (wasActive) focusEditor(owner?.id);
    return true;
  }

  async function closeOthers(id: string) {
    const owner = windowOf(session.get(), id);
    if (!owner) return;
    for (const other of owner.docIds.filter((d) => d !== id)) {
      if (!(await close(other))) return;
    }
    activate(id);
  }

  function markSaved(id: string, savedText: string, patch: Partial<DocMeta>) {
    const savedHash = textHash(savedText);
    dispatch({ type: 'patch', id, patch: { ...patch, savedHash, dirty: textHash(text(id)) !== savedHash } });
    writeDocument(id);
  }

  async function save(id?: string | null): Promise<boolean> {
    const m = meta(id);
    if (!m) return false;
    const ref = refs.get(m.id);
    if (m.origin !== 'file' || ref === undefined) return saveAs(m.id);
    const content = text(m.id);
    const data = encodeText(content, m.format);
    try {
      const { lastModified } = await files.write(ref, data, m.fileModified);
      markSaved(m.id, content, { lastSave: 'file', fileModified: lastModified });
      return true;
    } catch (error) {
      if (error instanceof FileConflictError) {
        const choice = await ask({
          title: `“${m.name}” changed on disk`,
          message: 'Another app modified this file after MyTXT opened it. Saving replaces that version.',
          actions: [
            { id: 'cancel', label: 'Cancel' },
            { id: 'saveAs', label: 'Save As…' },
            { id: 'overwrite', label: 'Replace', tone: 'danger' },
          ],
          cancelId: 'cancel',
        });
        if (choice === 'saveAs') return saveAs(m.id);
        if (choice !== 'overwrite') return false;
        try {
          const { lastModified } = await files.write(ref, data, undefined, true);
          markSaved(m.id, content, { lastSave: 'file', fileModified: lastModified });
          return true;
        } catch (inner) {
          console.error(inner);
        }
      } else if (error instanceof FilePermissionError) {
        notify(`MyTXT wasn’t allowed to write “${m.name}”. Use Save As to choose a location.`, 'error');
        return false;
      } else {
        console.error(error);
      }
      notify(`“${m.name}” couldn’t be saved. Your text is still here.`, 'error');
      return false;
    }
  }

  async function saveAs(id?: string | null): Promise<boolean> {
    const m = meta(id);
    if (!m) return false;
    const content = text(m.id);
    try {
      const result = await files.saveAs(m.name, m.kind, encodeText(content, m.format));
      if (!result) return false;
      if (result.method === 'download') {
        markSaved(m.id, content, { lastSave: 'download', name: result.name });
        notify(`Downloaded “${result.name}”.`);
        return true;
      }
      refs.set(m.id, result.ref);
      const kind = kindFromName(result.name);
      markSaved(m.id, content, { origin: 'file', lastSave: 'file', name: result.name, kind, fileModified: result.lastModified });
      if (kind !== m.kind) applyKind(m.id, kind);
      return true;
    } catch (error) {
      console.error(error);
      notify(`“${m.name}” couldn’t be saved. Your text is still here.`, 'error');
      return false;
    }
  }

  function viewShowing(id: string): EditorView | null {
    for (const [w, d] of shown) if (d === id) return views.get(w) ?? null;
    return null;
  }

  function applyKind(id: string, kind: DocKind) {
    const view = viewShowing(id);
    if (view) view.dispatch({ effects: reconfigureKind(kind) });
    else states.set(id, stateOf(id).update({ effects: reconfigureKind(kind) }).state);
    if (view && view === views.get(session.get().focusedId)) publishFormat(view.state, kind);
  }

  function rename(id: string, base: string) {
    const m = meta(id);
    if (!m || m.origin === 'file') return false;
    const name = renamedFile(base, m.kind);
    if (!name) return false;
    dispatch({ type: 'patch', id, patch: { name } });
    writeDocument(id);
    engine?.touch(id);
    return true;
  }

  function setKind(id: string, kind: DocKind) {
    const m = meta(id);
    if (!m || m.origin === 'file' || m.kind === kind) return;
    const name = renamedFile(m.name.replace(/\.[^.]+$/, ''), kind) ?? nextUntitledName([], kind);
    dispatch({ type: 'patch', id, patch: { kind, name } });
    applyKind(id, kind);
    writeDocument(id);
    engine?.touch(id);
  }

  function reorder(id: string, index: number) {
    dispatch({ type: 'reorder', id, index });
  }

  // --- windows ---------------------------------------------------------------

  function newWindow(options: { rect?: Rect; quiet?: boolean } = {}): string | null {
    const s = session.get();
    if (s.windows.length >= MAX_WINDOWS) {
      if (!options.quiet) notify(`MyTXT keeps up to ${MAX_WINDOWS} windows. Close one to open another.`);
      return null;
    }
    const from = focusedWindow(s);
    const rect = options.rect ?? placeNew(from?.zone ? (from.restore ?? defaultRect(canvas())) : (from?.rect ?? null), s.windows.map((w) => w.rect), canvas());
    const id = `w-${newId()}`;
    focusOnAttach.add(id);
    dispatch({ type: 'openWindow', window: { id, rect, zone: null, restore: null } });
    if (!options.rect) autoArrange();
    return id;
  }

  async function closeWindow(id: string): Promise<boolean> {
    const target = win(id);
    if (!target || session.get().windows.length === 1) return false;
    dispatch({ type: 'focusWindow', id });
    // Ask about each unsaved document in turn; any Cancel keeps the window.
    for (const docId of target.docIds) {
      const dm = meta(docId);
      if (dm && isCloudOnly(dm)) continue;
      if (!meta(docId)?.dirty && textHash(text(docId)) === meta(docId)?.savedHash) continue;
      dispatch({ type: 'activate', id: docId });
      if (!(await confirmLeave(docId))) {
        focusEditor(id);
        return false;
      }
    }
    const docIds = win(id)?.docIds ?? [];
    // Words the cloud lacks are set aside before their tabs go.
    const kept = new Map(docIds.flatMap((docId) => { const dm = meta(docId); const rec = dm ? retire(docId, dm) : null; return rec ? [[docId, rec] as const] : []; }));
    dispatch({ type: 'closeWindow', id });
    autoArrange();
    for (const docId of docIds) {
      forget(docId);
      const rec = kept.get(docId);
      if (rec) {
        await track(storage.putDocument(rec));
        engine?.touch(docId);
      } else await track(storage.deleteDocument(docId));
    }
    focusEditor();
    return true;
  }

  function focusWindow(id: string) {
    if (ui.get().dialog) return;
    dispatch({ type: 'focusWindow', id });
  }

  function placeWindow(id: string, rect: Rect, zone: Zone | null, restore?: Rect | null) {
    dispatch({ type: 'place', id, rect, zone, restore });
  }

  function toggleMaximize(id: string) {
    const w = win(id);
    if (!w) return;
    if (w.zone === 'max') placeWindow(id, normalizeRect(w.restore ?? defaultRect(canvas()), null, canvas()), null, null);
    else placeWindow(id, zoneRect('max', canvas()), 'max', w.zone ? w.restore : w.rect);
    dispatch({ type: 'focusWindow', id });
  }

  function tidyWindows() {
    arrangeWindows('auto');
  }

  function snapshotLayout() {
    layoutHistory = session.get().windows.map(({ id, rect, zone, restore }) => ({ id, rect, zone, restore }));
  }

  function layoutWindow(id: string, layout: WindowLayout) {
    const w = win(id);
    if (!w) return;
    const size = canvas();
    const floating = w.zone ? (w.restore ?? defaultRect(size)) : w.rect;
    dispatch({ type: 'focusWindow', id });
    if (layout === 'center' || layout === 'default') {
      placeWindow(id, layoutRect(layout, size, floating), null, null);
      return;
    }
    // The same layout again gives the window back its floating place.
    if (w.zone === layout) {
      placeWindow(id, normalizeRect(floating, null, size), null, null);
      return;
    }
    placeWindow(id, zoneRect(layout, size), layout, floating);
  }

  function arrangeWindows(mode: Arrangement) {
    const s = session.get();
    snapshotLayout();
    // Reading order of the current layout decides the order of the arrangement, so windows travel as little as possible.
    const ordered = readingOrder(s.windows);
    const rects = arrange(ordered.length, canvas(), mode);
    dispatch({ type: 'placeMany', rects: ordered.map((w, i) => ({ id: w.id, rect: rects[i] })) });
  }

  function matchWindowSizes(id: string) {
    const s = session.get();
    const model = win(id);
    if (!model || s.windows.length < 2) return;
    snapshotLayout();
    const others = s.windows.filter((w) => w.id !== id);
    const rects = matchSize(model.rect, others.map((w) => w.rect), canvas());
    dispatch({ type: 'placeMany', rects: others.map((w, i) => ({ id: w.id, rect: rects[i] })) });
  }

  function undoLayout(): boolean {
    if (!layoutHistory) return false;
    const size = canvas();
    for (const saved of layoutHistory) {
      if (!win(saved.id)) continue;
      placeWindow(saved.id, normalizeRect(saved.rect, saved.zone, size), saved.zone, saved.restore);
    }
    layoutHistory = null;
    return true;
  }

  function windowPlacedByHand() {
    layoutHistory = null;
    if (!ui.get().prefs.autoArrange) return;
    setPrefs({ autoArrange: false });
    notify('Auto Arrange is off while you place windows yourself.');
  }

  /** In Auto Arrange mode the canvas re-arranges itself when windows come and go. */
  function autoArrange() {
    if (ui.get().prefs.autoArrange && !ui.get().compact) arrangeWindows('auto');
  }

  function fitToCanvas() {
    const size = canvas();
    for (const w of session.get().windows) {
      const next = normalizeRect(w.rect, w.zone, size);
      if (next.x !== w.rect.x || next.y !== w.rect.y || next.w !== w.rect.w || next.h !== w.rect.h) placeWindow(w.id, next, w.zone);
    }
  }

  function moveToNewWindow(id: string): string | null {
    const owner = windowOf(session.get(), id);
    if (!owner || owner.docIds.length < 2) return null;
    const windowId = newWindow();
    if (!windowId) return null;
    focusOnAttach.delete(windowId);
    // The document's own view keeps its scroll position across the move.
    const view = views.get(owner.id);
    if (view && shown.get(owner.id) === id) scrollTops.set(id, view.scrollDOM.scrollTop);
    dispatch({ type: 'move', id, windowId });
    focusOnAttach.add(windowId);
    // Giving a cloud document a window of its own gives it a workspace of its own.
    if (meta(id)?.cloud) {
      dispatch({ type: 'patch', id, patch: { workspaceId: stampWindow(windowId) } });
      writeDocument(id);
      engine?.touch(id);
    }
    return windowId;
  }

  function moveToWindow(id: string, windowId: string): boolean {
    const owner = windowOf(session.get(), id);
    if (!owner || owner.id === windowId || !win(windowId)) return false;
    const view = views.get(owner.id);
    if (view && shown.get(owner.id) === id) scrollTops.set(id, view.scrollDOM.scrollTop);
    dispatch({ type: 'move', id, windowId });
    // Membership follows the deliberate move: a cloud document joins the destination's workspace.
    if (meta(id)?.cloud) {
      dispatch({ type: 'patch', id, patch: { workspaceId: stampWindow(windowId) } });
      writeDocument(id);
      engine?.touch(id);
    }
    return true;
  }

  function renameWorkspace(windowId: string, name: string) {
    dispatch({ type: 'renameWindow', id: windowId, name: name.trim().slice(0, 60) });
  }

  // --- preferences -----------------------------------------------------------

  function setPrefs(patch: Partial<EditorPrefs>) {
    const prefs = { ...ui.get().prefs, ...patch };
    savePrefs(prefs);
    patchUi({ prefs });
    const effect = setEditorPrefs(prefs);
    for (const [id, state] of states) if (!viewShowing(id)) states.set(id, state.update({ effects: effect }).state);
    for (const view of views.values()) view.dispatch({ effects: effect });
    if (patch.cloudAutosave !== undefined) engine?.autosaveChanged();
  }

  // --- local state -------------------------------------------------------------

  async function resetLocalState(options: { prefs: boolean }) {
    // Nothing pending may be written back after the erase.
    resetting = true;
    engine?.stop();
    detached.clear();
    publishDetached();
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    pendingText.clear();
    await Promise.all([...inflight]);
    try {
      await storage.clear();
    } finally {
      clearLocalKeys(options.prefs);
    }
  }

  // --- cloud -------------------------------------------------------------------

  /** The workspace a document is meant to belong to: its own record, else what the cloud last said. */
  const workspaceOf = (m: DocMeta): { id: string; name: string } | null => {
    const id = m.workspaceId !== undefined ? m.workspaceId : (m.cloud?.workspaceId ?? null);
    if (!id) return null;
    const presenting = session.get().windows.find((w) => w.workspaceId === id);
    return { id, name: presenting?.name ?? '' };
  };

  function reconcileCloud(id: string) {
    const m = meta(id);
    if (!m?.cloud) return;
    const unsynced = needsPush(m, text(id), workspaceOf(m)?.id ?? null);
    if (unsynced !== !!m.unsynced) dispatch({ type: 'patch', id, patch: { unsynced } });
  }

  function publishDetached() {
    const next = [...detached.values()].filter((d) => d.meta.cloud.account === account).map((d) => ({ id: d.meta.id, name: d.meta.name }));
    const prev = unsyncedStore.get();
    if (next.length === prev.length && next.every((n, i) => n.id === prev[i].id && n.name === prev[i].name)) return;
    unsyncedStore.set(next);
  }

  function persistDetached(id: string) {
    const d = detached.get(id);
    if (d) track(storage.putDocument({ meta: d.meta, text: d.text }));
    publishDetached();
  }

  function releaseDetached(id: string) {
    detached.delete(id);
    engine?.forget(id);
    track(storage.deleteDocument(id));
    publishDetached();
  }

  /** A window's workspace is created the first time it is needed, and then kept. */
  function stampWindow(windowId: string): string {
    const w = win(windowId);
    // A workspace belongs to the account that made it; another account gets a workspace of its own in the same window.
    if (w?.workspaceId && w.workspaceAccount === account) return w.workspaceId;
    const id = newId();
    dispatch({ type: 'bindWorkspace', id: windowId, workspaceId: id, account });
    return id;
  }

  /** Cloud fields for a document created now: only while signed in with Cloud Autosave on and access granted. */
  function cloudFieldsForNew(windowId: string, kind: DocKind): Partial<DocMeta> {
    if (!account || !engine || !ui.get().prefs.cloudAutosave || engine.state.get().access === 'denied') return {};
    const workspaceId = stampWindow(windowId);
    const cloudLink: CloudLink = { account, revision: 0, hash: '', name: '', kind, format: { ...DEFAULT_FORMAT }, workspaceId };
    return { cloud: cloudLink, unsynced: true, workspaceId };
  }

  /** If closing would leave the cloud behind, keep the words: they join the unsynced set. Returns the record to store. */
  function retire(id: string, m: DocMeta) {
    if (!m.cloud) return null;
    const t = text(id);
    if (!needsPush(m, t, workspaceOf(m)?.id ?? null)) return null;
    // Whatever it was, it is now a cloud-only document: file handles never leave the device and are not kept here.
    const home = { ...m, origin: 'new', dirty: t !== '', savedHash: EMPTY_HASH, lastSave: 'none', unsynced: true } as CloudMeta;
    detached.set(id, { meta: home, text: t });
    publishDetached();
    const s = engine?.state.get();
    if (!engine || !s || s.online === false || s.blocked || !ui.get().prefs.cloudAutosave) {
      notify(`“${m.name}” has changes the cloud doesn’t have yet. They stay on this device and sync when they can.`);
    }
    return { meta: home, text: t } satisfies StoredDocument;
  }

  /** Bring a closed document back into a window. With `local`, as an ordinary document with no cloud link. */
  function reattach(id: string, options: { local?: boolean; windowId?: string } = {}) {
    const d = detached.get(id);
    if (!d) return;
    detached.delete(id);
    publishDetached();
    const m: DocMeta = { ...d.meta, origin: 'new', dirty: d.text !== '', savedHash: EMPTY_HASH, lastSave: 'none', unsynced: true };
    if (options.local) {
      delete m.cloud;
      delete m.unsynced;
      delete m.workspaceId;
    }
    addDocument(m, d.text, { windowId: options.windowId, focus: false, activate: false });
  }

  function replaceText(id: string, content: string) {
    const state = stateOf(id);
    const head = Math.min(state.selection.main.head, content.length);
    const spec = { changes: { from: 0, to: state.doc.length, insert: content }, selection: { anchor: head }, userEvent: 'cloud.sync' };
    const view = viewShowing(id);
    if (view) view.dispatch(spec);
    else states.set(id, state.update(spec).state);
  }

  const syncDoc = (m: DocMeta, getText: () => string, sortOrder: number): SyncDoc => ({
    meta: m as CloudMeta,
    get text() {
      return getText();
    },
    workspace: workspaceOf(m),
    sortOrder,
  });

  const host: SyncHost = {
    docs() {
      const out: SyncDoc[] = [];
      for (const m of session.get().docs) {
        if (!m.cloud) continue;
        const owner = windowOf(session.get(), m.id);
        out.push(syncDoc(m, () => text(m.id), owner ? Math.max(0, owner.docIds.indexOf(m.id)) : 0));
      }
      for (const d of detached.values()) out.push(syncDoc(d.meta, () => d.text, 0));
      return out;
    },

    doc(id) {
      const m = session.get().docs.find((d) => d.id === id);
      if (m?.cloud) {
        const owner = windowOf(session.get(), id);
        return syncDoc(m, () => text(id), owner ? Math.max(0, owner.docIds.indexOf(id)) : 0);
      }
      const d = detached.get(id);
      return d ? syncDoc(d.meta, () => d.text, 0) : null;
    },

    link(id, link) {
      if (meta(id)) {
        // Only documents that are still in a cloud library: one the person just unlinked stays unlinked.
        if (!meta(id)!.cloud) return;
        dispatch({ type: 'patch', id, patch: { cloud: link } });
        writeDocument(id);
        return;
      }
      const d = detached.get(id);
      if (!d) return;
      d.meta = { ...d.meta, cloud: link };
      if (needsPush(d.meta, d.text, workspaceOf(d.meta)?.id ?? null)) persistDetached(id);
      else releaseDetached(id);
    },

    unlink(id) {
      if (meta(id)) {
        dispatch({ type: 'patch', id, patch: { cloud: undefined, unsynced: undefined, workspaceId: undefined } });
        writeDocument(id);
      } else if (detached.has(id)) {
        reattach(id, { local: true, windowId: session.get().focusedId });
      }
      engine?.forget(id);
    },

    adopt(id, remote, { keepLocalMeta, keepLocalText }) {
      if (!account) return;
      const link = linkFromRemote(account, remote);
      const m = meta(id);
      if (m?.cloud && m.cloud.account === account) {
        const fields = adoptedFields(m.cloud, m, remote, keepLocalMeta);
        const keepWorkspace = keepLocalMeta && m.workspaceId !== undefined && m.workspaceId !== m.cloud.workspaceId;
        adopting = true;
        try {
          dispatch({ type: 'patch', id, patch: { ...fields, cloud: link, workspaceId: keepWorkspace ? m.workspaceId : undefined } });
          if (!keepLocalText) replaceText(id, remote.content);
          if (fields.kind !== m.kind) applyKind(id, fields.kind);
        } finally {
          adopting = false;
        }
        writeDocument(id);
        return;
      }
      const d = detached.get(id);
      if (!d || d.meta.cloud.account !== account) return;
      const fields = adoptedFields(d.meta.cloud, d.meta, remote, keepLocalMeta);
      d.meta = { ...d.meta, ...fields, cloud: link, workspaceId: undefined };
      if (!keepLocalText) d.text = remote.content;
      if (needsPush(d.meta, d.text, workspaceOf(d.meta)?.id ?? null)) persistDetached(id);
      else releaseDetached(id);
    },

    keepCopy(fromId, name, copyText) {
      const src = meta(fromId) ?? detached.get(fromId)?.meta;
      const id = newId();
      const workspaceId = src ? (workspaceOf(src)?.id ?? null) : null;
      const kind = src?.kind ?? 'md';
      const format = { ...(src?.format ?? DEFAULT_FORMAT) };
      const link: CloudLink = { account: account!, revision: 0, hash: '', name, kind, format, workspaceId };
      const copy: DocMeta = { id, name, kind, origin: 'new', dirty: copyText !== '', savedHash: EMPTY_HASH, lastSave: 'none', format, cloud: link, unsynced: true, workspaceId };
      const owner = windowOf(session.get(), fromId);
      if (owner) addDocument(copy, copyText, { windowId: owner.id, focus: false, activate: false });
      else {
        detached.set(id, { meta: copy as CloudMeta, text: copyText });
        persistDetached(id);
      }
      return id;
    },

    drop(id) {
      const m = meta(id);
      // A document that also lives in a file may hold edits the file lacks: it stays open, as a file document.
      if (m && m.origin === 'file') host.unlink(id);
      else if (m) void discard(id);
      else releaseDetached(id);
    },

    notify,
  };

  function startEngine(uid: string) {
    if (!cloud.api) return;
    const e = createSyncEngine({
      api: cloud.api,
      host,
      account: uid,
      autosave: () => ui.get().prefs.cloudAutosave,
      onAuthLost: () => void revalidate(),
    });
    engine = e;
    e.setOnline(typeof navigator === 'undefined' || navigator.onLine !== false);
    e.state.subscribe(() => {
      if (engine === e) syncStore.set(e.state.get());
    });
    syncStore.set(e.state.get());
    void e.start().then(publishDetached);
  }

  async function revalidate() {
    // 'gone' flips the account state (which stops the engine); 'offline' waits for the network.
    if ((await cloud.auth.revalidate()) === 'ok') engine?.resume();
  }

  type Away = 'detach' | 'local';

  /**
   * Documents of an account leave the screen: ones the cloud holds are simply closed (the cloud has
   * them), ones it lacks are kept — set aside for that account, or left open as ordinary documents.
   * Documents that live in a file stay open as file documents either way.
   */
  function putAway(which: { only?: string; except?: string }, mode: Away): number {
    let kept = 0;
    for (const m of [...session.get().docs]) {
      if (!m.cloud) continue;
      if (which.only && m.cloud.account !== which.only) continue;
      if (which.except && m.cloud.account === which.except) continue;
      const pending = needsPush(m, text(m.id), workspaceOf(m)?.id ?? null);
      if (m.origin === 'file' || (pending && mode === 'local')) {
        host.unlink(m.id);
        if (pending) kept++;
        continue;
      }
      if (pending) kept++;
      void discard(m.id);
    }
    return kept;
  }

  function onAuthChange() {
    const a = cloud.auth.state.get();
    const uid = a.status === 'signed-in' && a.user ? a.user.id : null;
    if (uid === account) {
      // The token was renewed (by supabase-js on its own, or by a revalidation): a paused engine can go on.
      if (uid && engine?.state.get().blocked === 'auth' && Date.now() - lastResume > 10_000) {
        lastResume = Date.now();
        engine.resume();
      }
      return;
    }
    account = uid;
    engine?.stop();
    engine = null;
    syncStore.set(null);
    publishDetached();
    if (!uid) return;
    // A different account never inherits another account's documents.
    const kept = putAway({ except: uid }, 'detach');
    if (kept) notify('Another account’s unsynced changes were set aside on this device.');
    startEngine(uid);
  }

  function startCloud() {
    if (!cloud.configured) return;
    cloud.auth.state.subscribe(onAuthChange);
    onAuthChange();
    void cloud.auth.start();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        engine?.setOnline(true);
        if (engine?.state.get().blocked === 'auth') void revalidate();
      });
      window.addEventListener('offline', () => engine?.setOnline(false));
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void engine?.refresh();
        else void engine?.pushPending();
      });
    }
  }

  function windowForWorkspace(workspaceId: string | null): string {
    const s = session.get();
    if (workspaceId) {
      const match = s.windows.find((w) => w.workspaceId === workspaceId && w.workspaceAccount === account);
      if (match) return match.id;
      const focused = focusedWindow(s);
      if ((!focused.workspaceId || focused.workspaceAccount !== account) && focused.docIds.length === 0) {
        dispatch({ type: 'bindWorkspace', id: focused.id, workspaceId, account });
        return focused.id;
      }
      if (!ui.get().compact && s.windows.length < MAX_WINDOWS) {
        const id = newWindow({ quiet: true });
        if (id) {
          dispatch({ type: 'bindWorkspace', id, workspaceId, account });
          return id;
        }
      }
    }
    return s.focusedId;
  }

  async function openCloud(id: string): Promise<boolean> {
    if (session.get().docs.some((d) => d.id === id)) {
      activate(id);
      return true;
    }
    const held = detached.get(id);
    if (held && held.meta.cloud.account === account) {
      const windowId = windowForWorkspace(workspaceOf(held.meta)?.id ?? null);
      reattach(id, { windowId });
      activate(id);
      return true;
    }
    if (!engine || !account) {
      notify('Sign in to open cloud documents.', 'error');
      return false;
    }
    let remote: RemoteDoc | null;
    try {
      remote = await engine.fetch(id);
    } catch {
      notify('That document couldn’t be opened. Check your connection and try again.', 'error');
      return false;
    }
    if (!remote || remote.deleted_at) {
      notify('That document is no longer in your cloud library.', 'error');
      void engine.refresh();
      return false;
    }
    if (session.get().docs.some((d) => d.id === id)) {
      activate(id);
      return true;
    }
    const windowId = windowForWorkspace(remote.workspace_id);
    const cloudLink = linkFromRemote(account, remote);
    addDocument(
      { id, name: remote.name, kind: remote.kind, origin: 'new', dirty: remote.content !== '', savedHash: EMPTY_HASH, lastSave: 'none', format: cloudLink.format, cloud: cloudLink, unsynced: false, workspaceId: remote.workspace_id },
      remote.content,
      { windowId, selection: { anchor: 0, head: 0 } },
    );
    dispatch({ type: 'activate', id });
    return true;
  }

  /** Explicit, verified transfer of local documents into the account's library. Nothing local is removed. */
  async function moveToCloud(ids: string[]): Promise<MoveReport[]> {
    const reports: MoveReport[] = [];
    const sent: Array<{ id: string; name: string }> = [];
    for (const id of ids) {
      const m = meta(id);
      if (!m) continue;
      if (m.cloud) {
        reports.push({ id, name: m.name, status: 'skipped', detail: 'Already in a cloud library.' });
        continue;
      }
      if (!account || !engine || engine.state.get().access === 'denied') {
        reports.push({ id, name: m.name, status: 'failed', detail: 'Sign in to an account with cloud access first.' });
        continue;
      }
      if (text(id) === '') {
        reports.push({ id, name: m.name, status: 'skipped', detail: 'Empty documents are not uploaded.' });
        continue;
      }
      const owner = windowOf(session.get(), id);
      const workspaceId = owner ? stampWindow(owner.id) : null;
      const cloudLink: CloudLink = { account, revision: 0, hash: '', name: m.name, kind: m.kind, format: { ...m.format }, workspaceId };
      dispatch({ type: 'patch', id, patch: { cloud: cloudLink, unsynced: true, workspaceId } });
      writeDocument(id);
      sent.push({ id, name: m.name });
    }
    for (const { id, name } of sent) {
      await engine!.flush(id);
      const after = meta(id);
      const revision = after?.cloud?.revision ?? 0;
      const undo = (detail: string) => {
        // Not in the cloud: put the document back exactly as it was — local only.
        if (meta(id)?.cloud?.revision === 0) host.unlink(id);
        reports.push({ id, name, status: 'failed', detail });
      };
      if (!after?.cloud || revision === 0) {
        undo('It could not be uploaded. Nothing changed on this device.');
        continue;
      }
      try {
        const back = await engine!.fetch(id);
        // Compared with what the server acknowledged (the link's fingerprint), not with text typed during the upload.
        if (back && contentHash(back.content) === after.cloud.hash) reports.push({ id, name, status: 'verified', detail: 'Uploaded and read back.' });
        else reports.push({ id, name, status: 'failed', detail: back ? 'The cloud copy differs from this one; both were kept.' : 'The cloud copy could not be found.' });
      } catch {
        reports.push({ id, name, status: 'failed', detail: 'Uploaded, but it could not be read back to verify. It will keep syncing.' });
      }
    }
    return reports;
  }

  async function deleteFromCloud(id: string): Promise<boolean> {
    if (!engine) return false;
    const name = meta(id)?.name ?? detached.get(id)?.meta.name ?? engine.state.get().library?.find((d) => d.id === id)?.name ?? 'this document';
    const choice = await ask({
      title: `Delete “${name}” from your cloud library?`,
      message: 'It disappears from all your devices. A deleted copy is kept in the cloud for 30 days.',
      actions: [
        { id: 'cancel', label: 'Cancel' },
        { id: 'delete', label: 'Delete', tone: 'danger' },
      ],
      cancelId: 'cancel',
      focusId: 'cancel',
    });
    if (choice !== 'delete') return false;
    const result = await engine.remove(id);
    if (result === 'newer') {
      notify(`“${name}” was changed on another device, so it wasn’t deleted. Open it to review first.`, 'error');
      return false;
    }
    if (result === 'failed') {
      notify(`“${name}” couldn’t be deleted right now. Nothing was removed.`, 'error');
      return false;
    }
    const m = meta(id);
    if (m) {
      if (m.origin === 'file') host.unlink(id);
      else {
        dispatch({ type: 'patch', id, patch: { cloud: undefined, unsynced: undefined } });
        await discard(id);
      }
    } else if (detached.has(id)) releaseDetached(id);
    notify(`Deleted “${name}” from your cloud library.`);
    return true;
  }

  async function signOut(): Promise<boolean> {
    const leaving = account;
    if (!leaving) {
      await cloud.auth.signOut();
      return true;
    }
    if (engine?.busy()) await Promise.race([engine.syncNow(), new Promise((resolve) => setTimeout(resolve, 4000))]);
    const stillPending = host.docs().filter((d) => d.meta.cloud.account === leaving && needsPush(d.meta, d.text, d.workspace?.id ?? null));
    let mode: Away = 'detach';
    if (stillPending.length) {
      const n = stillPending.length;
      const answer = await askWithOption({
        title: `Sign out with ${n} unsynced ${n === 1 ? 'document' : 'documents'}?`,
        message: 'These changes have not reached your cloud library yet. They stay on this device and sync when you sign in to this account again.',
        details: stillPending.slice(0, 5).map((d) => d.meta.name),
        option: { label: 'Keep them open here as local documents instead' },
        actions: [
          { id: 'cancel', label: 'Cancel' },
          { id: 'out', label: 'Sign Out', tone: 'danger' },
        ],
        cancelId: 'cancel',
        focusId: 'cancel',
      });
      if (answer.action !== 'out') return false;
      mode = answer.option ? 'local' : 'detach';
    }
    const result = await cloud.auth.signOut();
    if (!result.ok) {
      notify(result.message, 'error');
      return false;
    }
    putAway({ only: leaving }, mode);
    return true;
  }

  // --- lifecycle -------------------------------------------------------------

  function restoreWindows(saved: StoredSession | LegacySession, known: Set<string>): SessionState {
    const size = canvas();
    if (!('version' in saved)) {
      const docIds = saved.order.filter((id) => known.has(id));
      const activeId = docIds.includes(saved.activeId ?? '') ? saved.activeId : (docIds[0] ?? null);
      const state = emptySession(defaultRect(size));
      state.windows[0] = { ...state.windows[0], docIds, activeId };
      return { ...state, docs: [] };
    }
    const windows: WindowState[] = saved.windows.map((w) => {
      const docIds = w.docIds.filter((id) => known.has(id));
      return {
        id: w.id,
        docIds,
        activeId: docIds.includes(w.activeId ?? '') ? w.activeId : (docIds[0] ?? null),
        rect: normalizeRect(w.rect, w.zone, size, saved.canvas),
        zone: w.zone,
        restore: w.restore ? normalizeRect(w.restore, null, size, saved.canvas) : null,
        z: w.z,
        workspaceId: w.workspaceId ?? null,
        workspaceAccount: w.workspaceAccount ?? null,
        ...(w.name ? { name: w.name } : {}),
      };
    });
    if (!windows.length) return emptySession(defaultRect(size));
    const focusedId = windows.some((w) => w.id === saved.focusedId) ? saved.focusedId : windows[0].id;
    return { docs: [], windows: windows.slice(0, MAX_WINDOWS), focusedId };
  }

  async function init() {
    patchUi({ ephemeral: !storage.durable });
    setEditorPrefs(ui.get().prefs);
    if (!storage.durable) notify('This browser isn’t keeping drafts. Save to a file to keep your work.', 'error');
    let restored = false;
    try {
      const saved = await storage.load();
      if (saved) {
        const byId = new Map(saved.documents.map((d) => [d.meta.id, d]));
        const layout = restoreWindows(saved.session, new Set(byId.keys()));
        const docs: DocMeta[] = [];
        for (const w of layout.windows) {
          for (const id of w.docIds) {
            const record = byId.get(id)!;
            states.set(id, createDocState(record.text, record.meta.kind, record.selection));
            if (record.ref !== undefined) refs.set(id, record.ref);
            // A file reference that did not survive storage means saving must ask again.
            const origin = record.meta.origin === 'file' && record.ref === undefined ? 'import' : record.meta.origin;
            const restored: DocMeta = { ...record.meta, origin, dirty: textHash(record.text) !== record.meta.savedHash };
            if (restored.cloud) restored.unsynced = needsPush(restored, record.text, workspaceOf(restored)?.id ?? null);
            docs.push(restored);
            byId.delete(id);
          }
        }
        // Records not referenced by any window are leftovers of an interrupted close — except cloud
        // documents the cloud has not caught up with: those are the unsynced words of a closed tab.
        for (const orphan of byId.values()) {
          const m = orphan.meta;
          if (m.cloud && needsPush(m, orphan.text, workspaceOf(m)?.id ?? null)) detached.set(m.id, { meta: { ...m, unsynced: true } as CloudMeta, text: orphan.text });
          else track(storage.deleteDocument(m.id));
        }
        dispatch({ type: 'reset', state: { ...layout, docs } });
        restored = docs.length > 0 || layout.windows.length > 1;
      }
    } catch (error) {
      console.error(error);
      notify('The previous session couldn’t be restored.', 'error');
    }
    // Writing can begin at once: the focused window's editor takes the keyboard as soon as it exists.
    focusOnAttach.add(session.get().focusedId);
    startCloud();
    // Every load opens with the identity; reduced motion goes straight to the work.
    patchUi({ ready: true, intro: isReduced() ? 'none' : restored ? 'restore' : 'full' });
  }

  return {
    session,
    writesInPlace: files.writesInPlace,
    init,
    attachView(windowId, view) {
      const old = views.get(windowId);
      if (old) windowOfView.delete(old);
      if (!view) {
        const docId = shown.get(windowId);
        if (old && docId && states.has(docId)) {
          states.set(docId, old.state);
          scrollTops.set(docId, old.scrollDOM.scrollTop);
        }
        views.delete(windowId);
        shown.delete(windowId);
        return;
      }
      views.set(windowId, view);
      windowOfView.set(view, windowId);
      shown.delete(windowId);
      show(windowId, win(windowId)?.activeId ?? null);
      if (focusOnAttach.delete(windowId)) focusEditor(windowId);
    },
    view: (windowId) => views.get(windowId ?? session.get().focusedId) ?? null,
    newDocument,
    openFiles,
    openDropped,
    activate,
    cycle,
    close,
    closeOthers,
    save,
    saveAs,
    rename,
    setKind,
    reorder,
    moveToNewWindow,
    moveToWindow,
    renameWorkspace,
    text,
    focusEditor,
    newWindow,
    closeWindow,
    focusWindow,
    placeWindow,
    toggleMaximize,
    tidyWindows,
    layoutWindow,
    arrangeWindows,
    matchWindowSizes,
    undoLayout,
    canUndoLayout: () => !!layoutHistory,
    windowPlacedByHand,
    resetLocalState,
    fitToCanvas,
    canvas,
    setPrefs,
    hasUnprotectedWork: () => pendingText.size > 0 || ((!storage.durable || storageFailed) && session.get().docs.some((d) => d.dirty)),
    flush,
    cloud: {
      configured: cloud.configured,
      problem: cloud.problem,
      auth: cloud.auth,
      sync: syncStore,
      unsynced: unsyncedStore,
      syncNow: async () => engine?.syncNow(),
      open: openCloud,
      moveToCloud,
      deleteFromCloud,
      signOut,
    },
  };
}
